import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from '../../components/feature/HeroSection';
import TrustBar from '../../components/feature/TrustBar';
import ServicesSection from '../../components/feature/ServicesSection';
import AIAnswerBlock from '../../components/feature/AIAnswerBlock';
import ProcessSection from '../../components/feature/ProcessSection';
import WhyChooseUs from '../../components/feature/WhyChooseUs';
import ServiceAreasSection from '../../components/feature/ServiceAreasSection';
import TestimonialsSection from '../../components/feature/TestimonialsSection';
import FAQSection from '../../components/feature/FAQSection';
import FinalCTA from '../../components/feature/FinalCTA';
import RevealOnScroll from '../../components/base/RevealOnScroll';
import { aboutFaqs } from '../../mocks/faqs';

const heroContent = {
  badge: 'Trusted Since 2008 · Houston, TX',
  headline: '16 Years Building Houston\'s Most Trusted Property Solutions Brand.',
  subheadline: 'Fair Pricing. Expert Work. No Surprises.',
  description: 'One Call Handyman Service was built on a simple promise: show up on time, do the job right, and treat every Houston homeowner with the respect they deserve. That promise has never changed.',
  primaryCTA: { text: 'Request Free Estimate', href: '#quote' },
  secondaryCTA: { text: 'Our Services', href: '/services' },
  backgroundImage: 'https://public.readdy.ai/ai/img_res/8b8b3353ec98532a3790234dd0ab2b82.jpg',
  stats: [
    { value: '16+', label: 'Years Serving Houston' },
    { value: '60%+', label: 'Repeat & Referral' },
    { value: '5.0 ★', label: 'Google Rating' },
    { value: '100%', label: 'Satisfaction Guarantee' },
  ],
};

const aiContent = {
  eyebrow: 'About One Call Handyman',
  title: 'The Company Behind the Name',
  lead: 'One Call Handyman Service is a top-rated Houston TX property solutions provider founded on integrity, craftsmanship, and genuine customer care. Over 16 years, we\'ve built our reputation one project at a time.',
  points: [
    {
      icon: 'ri-history-line',
      title: 'Our Story',
      body: 'Founded over 16 years ago to fill a critical gap in Houston TX — professional, trustworthy home improvement services at fair prices. We set out to be the handyman company Houston homeowners could always count on.',
    },
    {
      icon: 'ri-user-star-line',
      title: 'Owner — Anjela Sanchez',
      body: 'Anjela Sanchez personally oversees every project and maintains direct communication with each client. Her hands-on leadership and commitment to excellence define every interaction One Call Handyman has with Houston homeowners.',
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Our Commitment',
      body: 'Every project is approached with honesty, transparency, and a genuine commitment to quality. We don\'t consider a job complete until you\'re 100% satisfied — and we do the job right the first time.',
    },
    {
      icon: 'ri-refresh-line',
      title: '60%+ Repeat Business',
      body: 'More than 60% of our Houston work comes from repeat and referral clients. That statistic is the truest measure of our quality — Houston homeowners keep coming back and sending their friends and family.',
    },
    {
      icon: 'ri-tools-line',
      title: 'Fully Equipped Team',
      body: 'Our team arrives with all the latest tools and equipment to handle any home improvement challenge. We never cut corners on materials, methods, or professionalism.',
    },
    {
      icon: 'ri-medal-line',
      title: 'Satisfaction Guarantee',
      body: 'We stand behind every project with a full satisfaction guarantee. If you\'re not happy, we make it right. This commitment has earned us Houston\'s trust for over 16 years.',
    },
  ],
};

export default function AboutPage() {
  return (
    <main className="pb-16 lg:pb-0">
      <Navbar />
      <HeroSection content={heroContent} />
      <TrustBar />

      <section className="bg-[#F8F4EF] py-28 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 xl:gap-24 items-center">
            <RevealOnScroll direction="left">
              <div className="relative">
                <div className="w-full h-[500px] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20skilled%20home%20improvement%20craftsman%20working%20luxury%20residential%20renovation%20Houston%20Texas%20upscale%20interior%20expert%20precision%20tools%20quality%20workmanship%20warm%20natural%20light%20professional%20portrait%20vertical&width=1090&height=990&seq=about-team-001&orientation=portrait"
                    alt="One Call Handyman Service Houston team"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-5 -right-4 lg:-right-6 bg-[#BF9448] p-6">
                  <div className="font-serif text-white text-4xl font-bold leading-none">16+</div>
                  <div className="font-sans text-white/80 text-xs uppercase tracking-widest mt-2">Years Trusted</div>
                </div>
              </div>
            </RevealOnScroll>

            <RevealOnScroll direction="right">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-px bg-[#BF9448]" />
                <span className="text-[#BF9448] text-[10px] font-sans font-semibold tracking-[0.25em] uppercase">About Us</span>
              </div>
              <h2
                className="font-serif font-semibold text-[#0F0F0F] leading-tight mb-5"
                style={{ fontSize: 'clamp(1.6rem, 2.5vw, 2.25rem)' }}
              >
                One Call Handyman Service — Fair &amp; Honest Home Improvement in Houston. On Time and Done Right.
              </h2>
              <p className="font-sans text-[#7A7265] text-base leading-relaxed mb-5">
                With <strong className="text-[#0F0F0F]">16+ years of experience</strong> serving Houston TX and surrounding areas, we&apos;ve built our business on relationships and results — not just transactions.
              </p>
              <p className="font-sans text-[#7A7265] text-base leading-relaxed mb-5">
                <strong className="text-[#0F0F0F]">Over 60% of our business is repeat and referral</strong> because of the relationships we build through exceptional work and genuine customer service. We make sure you understand the process every step of the way — and we <strong className="text-[#0F0F0F]">do the job right the first time.</strong>
              </p>
              <p className="font-sans text-[#7A7265] text-base leading-relaxed mb-10">
                Owner <strong className="text-[#0F0F0F]">Anjela Sanchez</strong> is always available to answer questions and communicates with clients directly throughout every project.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-10">
                {[
                  { icon: 'ri-user-star-line', title: 'Experienced Team' },
                  { icon: 'ri-verified-badge-line', title: 'Satisfaction Guarantee' },
                  { icon: 'ri-tools-line', title: 'Fully Equipped' },
                  { icon: 'ri-calendar-check-line', title: '7 Days a Week' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="w-8 h-8 flex items-center justify-center border border-[#BF9448]/30 flex-shrink-0">
                      <i className={`${item.icon} text-[#BF9448] text-base`}></i>
                    </span>
                    <span className="font-sans text-[#0F0F0F] text-sm font-medium">{item.title}</span>
                  </div>
                ))}
              </div>
              <a
                href="#quote"
                className="bg-[#0F0F0F] hover:bg-[#1A1A1A] text-white font-sans text-xs font-semibold px-8 py-4 transition-colors duration-200 whitespace-nowrap cursor-pointer inline-block tracking-[0.12em] uppercase"
              >
                Get Free Quote
              </a>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      <AIAnswerBlock content={aiContent} />
      <ProcessSection />
      <WhyChooseUs
        image="https://readdy.ai/api/search-image?query=professional%20skilled%20home%20improvement%20craftsman%20explaining%20renovation%20plan%20homeowner%20luxury%20interior%20Houston%20Texas%20warm%20bright%20consultation%20friendly%20expert%20team%20discussion%20high%20end%20residential&width=1090&height=990&seq=about-why-001&orientation=portrait"
        title="The One Call Difference"
        subtitle="We don't just do the work — we communicate, educate, and ensure you're confident in every decision throughout your project."
      />
      <ServiceAreasSection
        title="Serving Houston Homeowners Everywhere"
        subtitle="One Call Handyman Service brings the same trusted standard to every community across Greater Houston TX."
      />
      <TestimonialsSection />
      <FAQSection
        faqs={aboutFaqs}
        title="About One Call Handyman — Answered"
        subtitle="What Houston homeowners want to know about who we are, how we work, and why we're different."
      />
      <ServicesSection
        title="What We Offer Houston Homeowners"
        subtitle="A complete range of professional home improvement services — all under one trusted brand."
        bg="cream"
      />
      <FinalCTA
        headline="Work with a Houston Contractor You Can Actually Trust"
        subheadline="16+ years. Fair pricing. Done right. Request your free estimate from One Call Handyman Service today."
      />
      <Footer />
    </main>
  );
}
